import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Login from './pages/Login';
import SignContract from './pages/SignContract';
import Dashboard from './pages/Dashboard';
import DashboardInvestisseur from './pages/DashboardInvestisseur';
import BilanInvestisseur from './pages/BilanInvestisseur';
import CompteResultatInvestisseur from './pages/CompteResultatInvestisseur';
import BilanDetail from './pages/BilanDetail';
import TableauComptable from './pages/TableauComptable';
import TableauDepenses from './pages/TableauDepenses';
import TableauInvestisseurs from './pages/TableauInvestisseurs';
import RepartitionCapital from './pages/RepartitionCapital';
import Factures from './pages/Factures';
import TableauTresorerie from './pages/TableauTresorerie';
import TresorerieInvestisseur from './pages/TresorerieInvestisseur';
import DetailFluxInvestisseur from './pages/DetailFluxInvestisseur';
import EvolutionTresorerieInvestisseur from './pages/EvolutionTresorerieInvestisseur';
import TableauProduits from './pages/TableauProduits';
import DetailTresorerieFlow from './pages/DetailTresorerieFlow';
import EvolutionTresorerie from './pages/EvolutionTresorerie';
import ArchivesFinancieres from './pages/ArchivesFinancieres';
import Organigramme from './pages/Organigramme';
import GraphiqueBenefices from './pages/GraphiqueBenefices';
import Entreprise from './pages/Entreprise';
import Employes from './pages/Employes';
import Prospects from './pages/Prospects';
import Inbox from './pages/Inbox';
import Clients from './pages/Clients';
import ClientDetail from './pages/ClientDetail';
import Contrats from './pages/Contrats';
import Affiches from './pages/Affiches';
import Calendrier from './pages/Calendrier';
import Echange from './pages/Echange';
import Emplacement from './pages/Emplacement';
import Occupation from './pages/Occupation';
import BoiteMail from './pages/BoiteMail';
import EmplacementDetail from './pages/EmplacementDetail';
import Revenus from './pages/Revenus';
import Admin from './pages/Admin';
import Franchise from './pages/Franchise';
// AudioVisuel module removed
import Suivi from './pages/SuiviHub';
import Tarifs from './pages/Tarifs';
import RetoursClients from './pages/RetoursClients';
import Paiements from './pages/Paiements';
import SuiviPanneaux from './pages/SuiviPanneaux';
import Planning from './pages/Planning';
import NosPanneaux from './pages/NosPanneaux';
import Reservations from './pages/Reservations';
import Utilisateurs from './pages/Utilisateurs';
import MonProfil from './pages/MonProfil';
import Technique from './pages/Technique';
import Conception from './pages/Conception';
import SaisieCommande from './pages/SaisieCommande';
import Fournisseurs from './pages/Fournisseurs';
import { Toaster } from './components/ui/sonner';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.baseURL = API;

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const IDLE_MS = 30 * 60 * 1000;
    const token = localStorage.getItem('token');
    const lastAct = Number(localStorage.getItem('sesam_last_activity') || 0);
    // Si l'utilisateur avait un token mais est resté inactif > 30 min → on nettoie tout
    if (token && lastAct && (Date.now() - lastAct) > IDLE_MS) {
      localStorage.removeItem('token');
      localStorage.removeItem('sesam_last_activity');
      setLoading(false);
      return;
    }
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      axios.get('/auth/me')
        .then(response => {
          setUser(response.data);
          setLoading(false);
        })
        .catch(() => {
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    console.log('=== LOGIN DEBUG ===');
    console.log('User data:', userData);
    console.log('User role:', userData?.role);
    console.log('==================');
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('sesam_last_activity');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  // Auto-logout après 30 min d'inactivité + à la reprise après veille de l'ordinateur.
  // "sesam_last_activity" est mis à jour à chaque interaction utilisateur (mouse/keyboard/touch/scroll)
  // et à chaque visibilitychange. Au montage, si trop de temps s'est écoulé, on déconnecte.
  useEffect(() => {
    if (!user) return;
    const IDLE_MS = 30 * 60 * 1000; // 30 minutes

    const bump = () => {
      try { localStorage.setItem('sesam_last_activity', String(Date.now())); } catch {}
    };
    bump();

    const check = () => {
      const raw = localStorage.getItem('sesam_last_activity');
      const last = Number(raw || 0);
      if (last && (Date.now() - last) > IDLE_MS) {
        handleLogout();
      }
    };

    // Événements d'activité
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach((ev) => window.addEventListener(ev, bump, { passive: true }));

    // Reprise depuis la veille / retour d'onglet
    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisibility);

    // Poll régulier (chaque minute) pour couvrir onglet inactif
    const timer = window.setInterval(check, 60 * 1000);

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, bump));
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <TechnicienGuard user={user} />
        <Routes>
          {/* Route publique de signature - accessible sans authentification */}
          <Route path="/sign/:token" element={<SignContract />} />
          <Route
            path="/"
            element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/dashboard" />}
          />
          <Route
            path="/dashboard"
            element={
              user ? 
                ((user.role === 'investisseur' || user.role === 'associe') ? 
                  <DashboardInvestisseur user={user} onLogout={handleLogout} /> :
                  (user.modules_allowed || []).includes('dashboard') ?
                  <Dashboard user={user} onLogout={handleLogout} /> :
                  user.role === 'technicien' ?
                  <Navigate to="/technique" replace /> :
                  (user.role === 'employee' || user.role === 'employer') ?
                  <Navigate to="/prospects" replace /> :
                  <Dashboard user={user} onLogout={handleLogout} />
                ) : 
                <Navigate to="/" />
            }
          />
          <Route
            path="/technique"
            element={user ? <Technique user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/conception"
            element={user ? <Conception user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/saisie-commande"
            element={user ? <SaisieCommande user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/fournisseurs"
            element={user ? <Fournisseurs user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/entreprise"
            element={
              !user ? <Navigate to="/" /> :
              ((user.role === 'employee' || user.role === 'employer') && !(user.modules_allowed || []).includes('entreprise')) ? <Navigate to="/prospects" replace /> :
              <Entreprise user={user} onLogout={handleLogout} />
            }
          />
          <Route
            path="/employes"
            element={user ? <Employes user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/prospects"
            element={user ? <Prospects user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/inbox"
            element={user ? <Inbox user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/clients"
            element={user ? <Clients user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/clients/:id"
            element={user ? <ClientDetail user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/contrats"
            element={user ? <Contrats user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/panneaux"
            element={user ? <NosPanneaux user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/affiches"
            element={user ? <Affiches user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/calendrier"
            element={user ? <Calendrier user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/echange"
            element={user ? <Echange user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/emplacement"
            element={user ? <Emplacement user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/boite-mail"
            element={user ? <BoiteMail user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/occupation"
            element={user ? <Occupation user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/emplacement/:id"
            element={user ? <EmplacementDetail user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/revenus"
            element={<Navigate to="/entreprise?tab=revenus" replace />}
          />
          <Route
            path="/admin"
            element={user ? <Admin user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/franchise"
            element={user ? <Franchise user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          {/* AudioVisuel route removed */}
          <Route
            path="/suivi"
            element={user ? <Suivi user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/retours-clients"
            element={user ? <RetoursClients user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/paiements"
            element={user ? <Paiements user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/tarifs"
            element={user ? <Tarifs user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/tableau-comptable"
            element={user ? <TableauComptable user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/tableau-depenses"
            element={user ? <TableauDepenses user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/tableau-investisseurs"
            element={user ? <TableauInvestisseurs user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/repartition-capital"
            element={user ? <RepartitionCapital user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/factures"
            element={user ? <Factures user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/tableau-tresorerie"
            element={user ? <TableauTresorerie user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/tresorerie-investisseur"
            element={user ? <TresorerieInvestisseur user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/tresorerie-investisseur/evolution"
            element={user ? <EvolutionTresorerieInvestisseur user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/tresorerie-investisseur/:type"
            element={user ? <DetailFluxInvestisseur user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/tableau-produits"
            element={user ? <TableauProduits user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/tresorerie/encaissements"
            element={user ? <DetailTresorerieFlow user={user} onLogout={handleLogout} type="enc" /> : <Navigate to="/" />}
          />
          <Route
            path="/tresorerie/decaissements"
            element={user ? <DetailTresorerieFlow user={user} onLogout={handleLogout} type="dec" /> : <Navigate to="/" />}
          />
          <Route
            path="/tresorerie/evolution"
            element={user ? <EvolutionTresorerie user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/organigramme"
            element={user ? <Organigramme user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/graphique-benefices"
            element={user ? <GraphiqueBenefices user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/bilan-investisseur"
            element={user ? <BilanInvestisseur user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/compte-resultat-investisseur"
            element={user ? <CompteResultatInvestisseur user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/bilan/actif"
            element={user ? <BilanDetail user={user} onLogout={handleLogout} type="actif" /> : <Navigate to="/" />}
          />
          <Route
            path="/bilan/passif"
            element={user ? <BilanDetail user={user} onLogout={handleLogout} type="passif" /> : <Navigate to="/" />}
          />
          <Route
            path="/archives/:type"
            element={user ? <ArchivesFinancieres user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/suivi-panneaux"
            element={user ? <SuiviPanneaux user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/planning"
            element={user ? <Planning user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          {/* /reservations redirige vers /suivi (module fusionné) */}
          <Route path="/reservations" element={<Navigate to="/suivi?type=reservation" replace />} />
          <Route
            path="/utilisateurs"
            element={user ? <Utilisateurs user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/mon-profil"
            element={user ? <MonProfil user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
          <Route
            path="/profil/:userEmail"
            element={user ? <MonProfil user={user} onLogout={handleLogout} /> : <Navigate to="/" />}
          />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

// Garde de navigation : modules autorisés par l'admin (ou liste par défaut du technicien).
const PATH_MODULES = new Set([
  'dashboard', 'entreprise', 'inbox', 'prospects', 'clients', 'contrats', 'paiements', 'suivi',
  'retours-clients', 'planning', 'tarifs', 'calendrier', 'echange', 'emplacement', 'panneaux', 'occupation',
  'technique', 'conception', 'saisie-commande', 'fournisseurs', 'utilisateurs', 'admin',
]);
const MODULE_ALIASES = { messagerie: 'echange', 'nos-panneaux': 'panneaux' };

function TechnicienGuard({ user }) {
  const location = useLocation();
  if (!user || user.role === 'admin') return null;
  const explicit = (user.modules_allowed || []).map((m) => MODULE_ALIASES[m] || m);
  let allowed = null;
  if (explicit.length) allowed = new Set(explicit);
  else if (user.role === 'technicien') allowed = new Set(['entreprise', 'technique', 'echange']);
  if (!allowed) return null;
  const seg = location.pathname.split('/')[1] || '';
  if (!PATH_MODULES.has(seg) || allowed.has(seg)) return null;
  const home = allowed.has('technique') && user.role === 'technicien' ? 'technique' : [...allowed].find((m) => PATH_MODULES.has(m) && m !== 'admin');
  return <Navigate to={home ? `/${home}` : '/mon-profil'} replace />;
}

export default App;
